import { useCallback, useEffect, useRef } from 'react';

import { IRestaurant } from '@rbi-ctg/store';
import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import useInterval from 'hooks/use-interval';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { useSmartAlertsContext } from 'state/smart-alert';
import { ISmartAlertsContext } from 'state/smart-alert/types';
import { logger } from 'utils/logger';
import { addSecondsToISOString } from 'utils/time-utils';

import { useStoreContext } from '..';
import { IStoreContext } from '../types';
import { clearStoreLocalTime, getAvailableDayParts, setStoreLocalTime } from '../utils';
import { normalizeAvailableDayParts } from '../utils/day-part-utils';

const REFRESH_INTERVAL = 1_000; // 1 second
const SMART_ALERTS_UPDATE_INTERVAL = 60_000; // 1 minute
const DAY_PARTS_AVAILABILITY_UPDATE_INTERVAL = 30_000; // 30 seconds
const smartAlertsCycleCount = { current: 0 };

const updateCycleCount = (cycleCount: { current: number }, interval: number) => {
  if (cycleCount.current >= interval / REFRESH_INTERVAL) {
    cycleCount.current = 0;
    return;
  }

  cycleCount.current += 1;
};

const handleStoreLocalTimeUpdate = (store: IRestaurant, baseTimes: BaseTimesRef) => {
  if (!store.localTime || !store.timezone) {
    logger.warn('no store local time or timezone set for the store');
    return null;
  }
  if (!baseTimes.storeLocalTime || !baseTimes.deviceTime) {
    setStoreLocalTime(store.localTime);
    return null;
  }

  const currentDeviceTime = Date.now();
  const elapsedSeconds = (currentDeviceTime - baseTimes.deviceTime) / 1000;

  const newLocalTime = addSecondsToISOString(
    baseTimes.storeLocalTime,
    elapsedSeconds,
    store.timezone
  );

  setStoreLocalTime(newLocalTime);

  return newLocalTime;
};

const handleSmartAlertsUpdate = (
  setCurrentStoreDate: ISmartAlertsContext['setCurrentStoreDate'],
  storeLocalTime: string | null
) => {
  if (smartAlertsCycleCount.current === 0) {
    if (!storeLocalTime) {
      setCurrentStoreDate(null);
      return;
    }
    setCurrentStoreDate(storeLocalTime);
  }

  updateCycleCount(smartAlertsCycleCount, SMART_ALERTS_UPDATE_INTERVAL);
};

interface IHandleAvailableDayPartsUpdateOptions {
  currentAvailableDayParts: IDayPartBoundary[] | undefined;
  cycleCount: { current: number };
  setAvailableDayParts: IStoreContext['setAvailableDayParts'];
  store: IRestaurant;
}

const handleAvailableDayPartsUpdate = ({
  currentAvailableDayParts,
  cycleCount,
  setAvailableDayParts,
  store,
}: IHandleAvailableDayPartsUpdateOptions) => {
  if (cycleCount.current === 0) {
    const nextAvailableDayParts = normalizeAvailableDayParts(getAvailableDayParts(store));
    if (JSON.stringify(currentAvailableDayParts) !== JSON.stringify(nextAvailableDayParts)) {
      setAvailableDayParts(nextAvailableDayParts);
    }
  }

  updateCycleCount(cycleCount, DAY_PARTS_AVAILABILITY_UPDATE_INTERVAL);
};

type BaseTimesRef = {
  storeLocalTime: string | null;
  deviceTime: number | null;
};

export const StoreTimer = () => {
  const {
    store,
    noStoreSelected,
    setAvailableDayParts,
    availableDayParts,
    fetchStore,
  } = useStoreContext();
  const storeLocalTime = useRef<string | null>(null);
  const dayPartCycleCount = useRef(0);
  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();
  const { setCurrentStoreDate } = useSmartAlertsContext();
  const baseTime = useRef<BaseTimesRef>({
    storeLocalTime: null,
    deviceTime: null,
  });

  const runStoreIntervalLogic = useCallback(() => {
    if (noStoreSelected) {
      clearStoreLocalTime();
    } else {
      storeLocalTime.current = handleStoreLocalTimeUpdate(store, baseTime.current);
      handleSmartAlertsUpdate(setCurrentStoreDate, storeLocalTime.current);
      handleAvailableDayPartsUpdate({
        currentAvailableDayParts: availableDayParts,
        cycleCount: dayPartCycleCount,
        setAvailableDayParts,
        store,
      });
      // TODO: use more handlers here as needed like
      // handleStoreClose(store);
      // handleServiceModeClose(store);
    }
  }, [noStoreSelected, store, setCurrentStoreDate, setAvailableDayParts, availableDayParts]);

  /**
   * This useEffect sets the base times for the store's local time (storeLocalTime)
   * and the device's time (deviceTime). By tracking these values, we can calculate
   * the correct store time even when the app goes to the background and the timer stops,
   * avoiding incorrect date displays when returning to the foreground.
   */
  useEffect(() => {
    smartAlertsCycleCount.current = 0;
    if (noStoreSelected) {
      clearStoreLocalTime();
      baseTime.current.storeLocalTime = null;
      baseTime.current.deviceTime = null;
    } else if (store?.localTime) {
      baseTime.current.storeLocalTime = store.localTime;
      baseTime.current.deviceTime = Date.now();
    } else {
      logger.warn('No store local time available at initialization');
    }
  }, [store.localTime, noStoreSelected]);

  /**
   * This useEffect listens for when the app returns from the background, and refetches the store
   * to get the latest store data, including the updated store time.
   * This updates the baseTime ref (when store changes), ensuring the store time stays accurate.
   */
  useEffect(() => {
    addAppStateChangeListeners(() => {
      if (!noStoreSelected && store.number) {
        fetchStore(store.number);
      } else {
        clearStoreLocalTime();
      }
    });

    return () => {
      removeAppStateChangeListeners();
    };
  }, [
    addAppStateChangeListeners,
    fetchStore,
    noStoreSelected,
    removeAppStateChangeListeners,
    store.number,
  ]);

  // Run the logic immediately when the store number changes,
  // so day parts update right away instead of waiting for the next interval
  useEffect(() => {
    dayPartCycleCount.current = 0;
    runStoreIntervalLogic();
  }, [runStoreIntervalLogic, store.number]);

  useInterval(() => {
    runStoreIntervalLogic();
  }, REFRESH_INTERVAL);

  return null;
};
